/**
 * Motion tokens and ready `motion` variants, POLISH §2. KARDIS ships this file; lanes import
 * from it, they never redefine a duration, ease, spring or variant locally.
 */
import type { Transition, Variants } from 'motion/react'

/** Durations in ms, POLISH §1/§2. */
export const dur = {
  tap: 90,
  micro: 160,
  ui: 240,
  screen: 320,
  celebrate: 900,
  cinematic: 2400,
} as const

export type DurKey = keyof typeof dur

/** Numeric cubic-bezier control points, for code that needs the raw curve. */
export const easeBezier = {
  standard: [0.2, 0.8, 0.2, 1],
  emphasized: [0.05, 0.7, 0.1, 1],
  exit: [0.3, 0, 0.8, 0.15],
} as const satisfies Record<string, [number, number, number, number]>

/** CSS `cubic-bezier(...)` strings for the same curves, for use in plain CSS/style props. */
export const ease = {
  standard: `cubic-bezier(${easeBezier.standard.join(',')})`,
  emphasized: `cubic-bezier(${easeBezier.emphasized.join(',')})`,
  exit: `cubic-bezier(${easeBezier.exit.join(',')})`,
} as const

export type EaseKey = keyof typeof ease

/** Spring presets shaped for `motion`'s `transition` objects, POLISH §1.3/§2. */
export const spring = {
  ui: { type: 'spring', stiffness: 300, damping: 20 } as const,
  character: { type: 'spring', stiffness: 180, damping: 14 } as const,
  soft: { type: 'spring', stiffness: 120, damping: 16 } as const,
} satisfies Record<string, Transition>

export type SpringKey = keyof typeof spring

/** Press feedback, POLISH §1.1/§2. */
export const press = {
  scale: 0.96,
  hover: 'none',
  focusRing: '3px solid var(--mint-deep)',
} as const

/** Shake, POLISH §2: player hits in minigames only, off under reduced motion. */
export const shake = {
  maxPx: 4,
  durationMs: 120,
} as const

/** True when the viewer asked the OS for reduced motion. Re-read on every call: no cached stale value. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Every touch answers within one frame, POLISH §1.1: scale to `press.scale`, spring back with `spring.ui`. */
export const pressable: Variants = {
  rest: { scale: 1 },
  pressed: { scale: press.scale, transition: spring.ui },
}

/** Route transitions, POLISH §1.5: 320 ms in with emphasized ease, slide + slight scale. */
export const screenEnter: Variants = {
  initial: { opacity: 0, y: 24, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: dur.screen / 1000, ease: easeBezier.emphasized },
  },
}

/** Route transitions, POLISH §1.5: 280 ms out with exit ease, slide + slight scale, reversed. */
export const screenExit: Variants = {
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: {
    opacity: 0,
    y: -16,
    scale: 0.98,
    transition: { duration: 280 / 1000, ease: easeBezier.exit },
  },
}

/** Sheets rise from the bottom, POLISH §1.5. */
export const sheet: Variants = {
  initial: { opacity: 0, y: '100%' },
  animate: { opacity: 1, y: 0, transition: spring.ui },
  exit: { opacity: 0, y: '100%', transition: { duration: dur.ui / 1000, ease: easeBezier.exit } },
}

/** Modals scale from 0.92 with a dimmed backdrop, POLISH §1.5. */
export const modal: Variants = {
  initial: { opacity: 0, scale: 0.92 },
  animate: { opacity: 1, scale: 1, transition: spring.ui },
  exit: {
    opacity: 0,
    scale: 0.92,
    transition: { duration: dur.ui / 1000, ease: easeBezier.exit },
  },
}

/** Cards and sheets overshoot 4 to 6 percent then settle, POLISH §1.3. */
export const pop: Variants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: {
    scale: [0.9, 1.05, 1],
    opacity: 1,
    transition: { duration: dur.micro / 1000, ease: easeBezier.standard, times: [0, 0.6, 1] },
  },
}

/**
 * POLISH §1.7: under reduced motion every variant collapses to a 150-300 ms opacity fade only,
 * no travel, no scale, no particles - and never drops a state or a reward, only its motion.
 */
const REDUCED_MOTION_MS = 200

function toOpacityOnly(variants: Variants): Variants {
  const reduced: Variants = {}
  for (const [name, value] of Object.entries(variants)) {
    if (typeof value !== 'object' || value === null) {
      reduced[name] = value
      continue
    }
    const opacity =
      'opacity' in value ? value.opacity : name === 'initial' || name === 'exit' ? 0 : 1
    reduced[name] = {
      opacity,
      transition: { duration: REDUCED_MOTION_MS / 1000, ease: easeBezier.standard },
    }
  }
  return reduced
}

/**
 * `useReducedMotion`-aware helper: returns the given variants unchanged, or an opacity-only
 * 200 ms fallback when `prefers-reduced-motion` is set (POLISH §1.7). Re-checks the media query
 * on every call, so it stays correct across a live OS setting change.
 */
export function withMotion(variants: Variants): Variants {
  return prefersReducedMotion() ? toOpacityOnly(variants) : variants
}
